/**
 * Cobrança — quanto o WhatsApp custou e se a conta está pronta para enviar.
 *
 * Duas decisões de tela que vêm do domínio, não de gosto:
 *
 *  1. "Custo desconhecido" aparece como aviso, nunca como R$ 0,00. A Meta
 *     omite custo em conta faturada por parceiro, e mostrar zero faria o
 *     cliente acreditar que o mês foi de graça.
 *  2. A prontidão da conta vem ANTES do gasto. Quem abre esta tela recém
 *     conectado quer saber por que a mensagem não sai; quem já está rodando
 *     passa reto pelo bloco verde.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Wallet, MessageSquare, TrendingUp, RefreshCw, CheckCircle2, AlertTriangle, XCircle, HelpCircle, Receipt, CreditCard,
} from "lucide-react";
import { formatarMoeda } from "@shared/whatsapp-billing";

const PERIODOS = [
  { value: 7, label: "Últimos 7 dias" },
  { value: 30, label: "Últimos 30 dias" },
  { value: 90, label: "Últimos 90 dias" },
  { value: 180, label: "Últimos 180 dias" },
  { value: 365, label: "Último ano" },
];

const ICONE_NIVEL = {
  ok: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  atencao: <AlertTriangle className="h-4 w-4 text-amber-600" />,
  bloqueado: <XCircle className="h-4 w-4 text-red-600" />,
  desconhecido: <HelpCircle className="h-4 w-4 text-muted-foreground" />,
} as const;

function Kpi({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span className="text-xs uppercase tracking-wide">{label}</span>
        </div>
        <p className="mt-1 text-2xl font-semibold">{value}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function BlocoProntidao() {
  const prontidao = trpc.billing.prontidao.useQuery(undefined, { staleTime: 120_000, retry: false });

  if (prontidao.isLoading) {
    return <Card><CardContent className="py-6 text-sm text-muted-foreground">Verificando a conta na Meta…</CardContent></Card>;
  }
  if (prontidao.error) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Não foi possível verificar a conta agora: {prontidao.error.message}
        </CardContent>
      </Card>
    );
  }

  const itens = prontidao.data?.itens ?? [];
  const pendentes = itens.filter((i) => i.nivel === "bloqueado" || i.nivel === "atencao");
  // "Não sei" não é "tudo certo". Se a Meta não respondeu sobre algum item,
  // o selo verde esconderia justamente a pendência que trava o onboarding —
  // é a mesma confusão que o painel de custo evita com o custo ausente.
  const desconhecidos = itens.filter((i) => i.nivel === "desconhecido");

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Situação da conta na Meta
          {pendentes.length > 0 ? (
            <span className="ml-2 text-sm font-normal text-amber-600">{pendentes.length} ponto(s) de atenção</span>
          ) : desconhecidos.length > 0 ? (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {desconhecidos.length} item(ns) que a Meta não informou
            </span>
          ) : (
            <span className="ml-2 text-sm font-normal text-emerald-600">tudo certo</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {itens.map((item) => (
          <div key={item.chave} className="flex gap-3">
            <div className="mt-0.5">{ICONE_NIVEL[item.nivel]}</div>
            <div className="min-w-0">
              <p className="text-sm font-medium">{item.titulo}</p>
              <p className="text-sm text-muted-foreground">{item.detalhe}</p>
              {item.acao && <p className="mt-0.5 text-xs text-muted-foreground">{item.acao}</p>}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Assinatura do LeadConversa (Stripe). Só o essencial: em que situação está e
 * o botão que abre o Portal do Cliente.
 *
 * Existe porque sem ele o cliente com cartão vencido fica sem saída: o
 * checkout recusa quem já tem assinatura (para não cobrar duas vezes) e não
 * haveria nenhum outro caminho para trocar o cartão.
 */
function BlocoAssinatura({ podeGerenciar }: { podeGerenciar: boolean }) {
  const assinatura = trpc.payment.assinatura.useQuery(undefined, { enabled: podeGerenciar, retry: false });
  const portal = trpc.payment.portal.useMutation({
    onSuccess: ({ url }) => { window.location.href = url; },
  });

  if (!podeGerenciar || assinatura.isLoading) return null;
  const a = assinatura.data;
  if (!a) return null;

  const SITUACAO: Record<string, string> = {
    active: "Ativa",
    trialing: "Em teste",
    past_due: "Pagamento em atraso",
    unpaid: "Pagamento pendente",
  };
  const emRisco = a.status === "past_due" || a.status === "unpaid";

  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base">Seu plano no LeadConversa</CardTitle></CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        <div className="mr-auto text-sm">
          <p className={emRisco ? "font-medium text-amber-600" : "font-medium"}>
            {SITUACAO[a.status] ?? a.status}
          </p>
          <p className="text-muted-foreground">
            {a.cancelaNoFim
              ? "Cancelamento agendado para o fim do período atual."
              : a.periodoFim
                ? `Próxima cobrança em ${new Date(a.periodoFim).toLocaleDateString("pt-BR")}.`
                : "Assinatura em dia."}
          </p>
        </div>
        <Button variant="outline" onClick={() => portal.mutate()} disabled={portal.isPending}>
          {portal.isPending ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
          Gerenciar assinatura
        </Button>
        {portal.error && <p className="w-full text-sm text-red-600">{portal.error.message}</p>}
      </CardContent>
    </Card>
  );
}

function CobrancaContent() {
  const [dias, setDias] = useState(30);
  // O Portal do Stripe é `tenantAdminProcedure`: pedir a assinatura para quem
  // não é admin só geraria um 403 barulhento na tela.
  const { user } = useAuth();
  const ehAdmin = user?.tenantRole === "owner" || user?.tenantRole === "admin";
  const utils = trpc.useUtils();
  const resumo = trpc.billing.resumo.useQuery({ dias }, { staleTime: 60_000, retry: false });
  const sincronizar = trpc.billing.sincronizar.useMutation({
    onSuccess: () => {
      utils.billing.resumo.invalidate();
      utils.billing.prontidao.invalidate();
    },
  });

  const d = resumo.data;
  const moeda = d?.moeda ?? null;
  const valor = (v: number) => formatarMoeda(v, moeda);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold">Cobrança</h1>
          <p className="text-sm text-muted-foreground">
            O que a Meta cobra pelas mensagens do WhatsApp deste workspace.
          </p>
        </div>

        <Select value={String(dias)} onValueChange={(v) => setDias(Number(v))}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODOS.map((p) => <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={() => sincronizar.mutate({ dias })} disabled={sincronizar.isPending}>
          <RefreshCw className={`mr-2 h-4 w-4 ${sincronizar.isPending ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {sincronizar.error && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {sincronizar.error.message}
        </div>
      )}

      {/* Assinatura antes do consumo: é a única coisa desta tela que pode
          estar bloqueando o cliente agora. */}
      <BlocoAssinatura podeGerenciar={ehAdmin} />

      <BlocoProntidao />

      {resumo.error ? (
        <Card><CardContent className="py-6 text-sm text-muted-foreground">{resumo.error.message}</CardContent></Card>
      ) : (
        <>
          {/* Custo desconhecido nunca vira zero na tela: esse é o aviso que
              impede alguém de fechar o mês com o número errado. */}
          {d && !d.custoConhecido && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              A Meta não informou o custo de {d.mensagensSemCusto.toLocaleString("pt-BR")} mensagem(ns) deste período — acontece
              quando a conta é faturada por um parceiro. Os valores abaixo estão incompletos.
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              icon={<Wallet className="h-4 w-4" />}
              label="Gasto no período"
              value={valor(d?.total ?? 0)}
              hint={moeda ? `em ${moeda}` : undefined}
            />
            {/* Pagas, não o total: numa conta de atendimento as gratuitas são
                a maioria e o custo por mensagem sairia ~50x menor. */}
            <Kpi
              icon={<MessageSquare className="h-4 w-4" />}
              label="Mensagens pagas"
              value={(d?.mensagensPagas ?? 0).toLocaleString("pt-BR")}
              hint={
                d && d.mensagensPagas > 0
                  ? `${valor(d.total / d.mensagensPagas)} por mensagem · ${(d.mensagens - d.mensagensPagas).toLocaleString("pt-BR")} gratuitas`
                  : undefined
              }
            />
            <Kpi
              icon={<Receipt className="h-4 w-4" />}
              label="Mês atual"
              value={d?.mesAtual ? valor(d.mesAtual.total) : "—"}
              hint={
                d?.mesAtual
                  ? `${d.mesAtual.mensagens.toLocaleString("pt-BR")} mensagens`
                  : "escolha um período que cubra o mês inteiro"
              }
            />
            <Kpi
              icon={<TrendingUp className="h-4 w-4" />}
              label="Projeção do mês"
              value={d?.mesAtual?.projecao != null ? valor(d.mesAtual.projecao) : "—"}
              hint={d?.mesAtual?.projecao != null ? "pela média dos dias fechados" : "ainda não há dia fechado no mês"}
            />
          </div>

          {d?.modo === "reseller" && d.markupPercent > 0 && (
            <p className="text-xs text-muted-foreground">
              Os valores incluem {d.markupPercent.toLocaleString("pt-BR")}% de taxa de serviço sobre o custo da Meta.
            </p>
          )}

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Gasto por dia</CardTitle></CardHeader>
            <CardContent>
              {(d?.porDia?.length ?? 0) === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Nenhum consumo registrado. Use <strong>Atualizar</strong> para buscar os dados na Meta.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={d!.porDia}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tickFormatter={(v: string) => v.slice(5)} fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip
                      formatter={(v: number) => valor(Number(v))}
                      labelFormatter={(l: string) => new Date(`${l}T12:00:00`).toLocaleDateString("pt-BR")}
                    />
                    <Bar dataKey="total" name="Gasto" fill="#22c55e" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Por categoria</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Categoria</th>
                    <th className="px-4 py-2 text-right font-medium">Mensagens</th>
                    <th className="px-4 py-2 text-right font-medium">Gratuitas</th>
                    <th className="px-4 py-2 text-right font-medium">Gasto</th>
                  </tr>
                </thead>
                <tbody>
                  {(d?.porCategoria ?? []).map((c) => (
                    <tr key={c.categoria} className="border-b last:border-0">
                      <td className="px-4 py-2">{c.rotulo}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{c.mensagens.toLocaleString("pt-BR")}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                        {c.mensagensGratuitas.toLocaleString("pt-BR")}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {c.custoConhecido ? valor(c.total) : <span className="text-muted-foreground">não informado</span>}
                      </td>
                    </tr>
                  ))}
                  {(d?.porCategoria?.length ?? 0) === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Sem dados no período.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            Fonte: relatório de preços da Meta. Os valores são aproximados e podem ser ajustados por ela por alguns dias —
            a fatura oficial continua sendo a do Gerenciador de Negócios.
            {d?.atualizadoEm && ` Atualizado em ${new Date(d.atualizadoEm).toLocaleString("pt-BR")}.`}
          </p>
        </>
      )}
    </div>
  );
}

export default function Cobranca() {
  return (
    <PermissionGuard permission="billing:view">
      <DashboardLayout>
        <CobrancaContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
